/**
 * INSTRUCTIONS FOR TRANSLATIONS
 * Translations must be defined in a message.js file. Check the LiveDashboard folder for an example.
 * There you define ids and keys for every translation as well as a default text. The language
 * for the default texts is english! Then you have to define a translation for every language.
 * In the translations folder is a file for every language. There you use the id that you used
 * in the message.js file to define the translation. Afterwards you can use the translation object
 * from the message.js file in the components.
 */
import base64 from 'base-64';
import { type ReactNode, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { bindActionCreators, type AnyAction, type Dispatch } from 'redux';

import { NotificationContainer } from 'react-notifications';
import { LocalStorageKey } from 'shared/types';
import { localStorage } from 'shared/utils';

import {
  getEmployees,
  getInvoiceTemplates,
  getMailTemplates,
  getProductList,
  getShopList,
  getUserPermissions,
  setUserRole,
} from '../../actions/actions';

import { OnBoarding } from '../OnBoarding';
import Footer, { type ShopCollection } from './Footer';

import { ContentWrapper } from './styles';
import type { RootReduxState } from '../../utils/types';

interface DashboardStateProps {
  shops: ShopCollection;
  shopsRecieved: boolean;
  role?: number;
  shopsLoading?: boolean;
}

interface DashboardDispatchProps {
  setUserRole: (userRole: number) => AnyAction;
  getProductList: () => AnyAction;
  getShopList: () => AnyAction;
  getUserPermissions: () => AnyAction;
  getInvoiceTemplates: (shopId?: string) => AnyAction;
  getMailTemplates: (shopId?: string) => AnyAction;
  getEmployees: () => AnyAction;
}

interface DashboardOwnProps {
  children: ReactNode;
  withShopOnboarding?: boolean;
}

export type DashboardProps = DashboardStateProps &
  DashboardDispatchProps &
  DashboardOwnProps;

interface TokenPayload {
  role: number;
}

export const Dashboard = ({
  shopsRecieved,
  shops,
  withShopOnboarding = true,
  role,
  getProductList,
  getShopList,
  getUserPermissions,
  setUserRole,
  getMailTemplates,
  getInvoiceTemplates,
  getEmployees,
  children,
}: DashboardProps) => {
  const { pathname } = useLocation();
  const shopsWereReceivedOnMount = useRef(shopsRecieved);

  useEffect(loadUserCatalogs, [
    getProductList,
    getShopList,
    getUserPermissions,
    setUserRole,
  ]);
  useEffect(loadAccountingCatalogs, [
    shopsRecieved,
    role,
    getMailTemplates,
    getInvoiceTemplates,
    getEmployees,
  ]);

  return (
    <>
      {shopsRecieved && (
        <ContentWrapper dashboard={pathname.endsWith('/dashboard')}>
          {children}
        </ContentWrapper>
      )}
      <Footer path={pathname} shops={shops} />
      <NotificationContainer />
      <OnBoarding withShopCookie={withShopOnboarding} />
    </>
  );

  function loadUserCatalogs() {
    const token = localStorage.getItem(LocalStorageKey.TOKEN);
    const { role: userRole }: TokenPayload = JSON.parse(
      base64.decode(token!.split('.')[1]),
    );
    setUserRole(userRole);
    getProductList();
    if (!shopsWereReceivedOnMount.current) getShopList();
    getUserPermissions();

    return () => {
      const script = document.querySelector('#atlwdg-trigger');
      if (script) {
        script.remove();
      }
    };
  }

  function loadAccountingCatalogs() {
    if (!shopsRecieved || role === undefined || role === 3) return;
    getMailTemplates();
    getInvoiceTemplates();
    getEmployees();
  }
};

// REDUX
const mapStateToProps = (state: RootReduxState): DashboardStateProps => ({
  shops: state.getIn([
    'customerBackendDashboard',
    'userData',
    'shops',
  ]) as ShopCollection,
  shopsRecieved: state.getIn([
    'customerBackendDashboard',
    'userData',
    'shopsRecieved',
  ]) as boolean,
  role: state.getIn(['customerBackendDashboard', 'userData', 'role']) as
    | number
    | undefined,
  shopsLoading: state.getIn([
    'customerBackendDashboard',
    'userData',
    'shopsLoading',
  ]) as boolean | undefined,
});

const matchDispatchToProps = (dispatch: Dispatch): DashboardDispatchProps =>
  bindActionCreators(
    {
      setUserRole,
      getProductList,
      getUserPermissions,
      getShopList,
      getInvoiceTemplates,
      getMailTemplates,
      getEmployees,
    },
    dispatch,
  );

export default connect(mapStateToProps, matchDispatchToProps)(Dashboard);
